import { useContext, useState } from 'react';
import styled from '@emotion/styled';
import { Button } from '@mui/material';

import { ConfigContext } from '@/contextAPI/configContext';
import { useAppSelector } from '@/redux/hooks';
import { OrderJobTask } from '@/redux/slices/slice_types';
import { selectCanWriteTasks } from '@/redux/selectors/authSelectors';
import { selectBulkErrors, selectCurrentTableBulkActions, selectCurrentTableBulkActionsByStatusStage } from '@/redux/selectors/dataTableSelectors';
import { displayBulkActionIdent } from '@/_utils/utils';
import { LabelValue, PolarisIcon } from '@/exports/columnCommonComponents';
import { IconDictionary } from '@/components/_common/PolarisIcon';
import BulkActionErrorModal from '@/components/_common/BulkActionErrorModal';
import EntityActionDialog from '@/components/_common/EntityActionDialog';

type TaskDrawerBulkActionsProps = {
  task: OrderJobTask;
  handleRefresh: () => void;
};

const TaskDrawerBulkActions = ({ task, handleRefresh }: TaskDrawerBulkActionsProps) => {
  const [dialogAction, setDialogAction] = useState<string | null>(null);
  const actions = useAppSelector(selectCurrentTableBulkActions);
  const actionsByStatusStage = useAppSelector(selectCurrentTableBulkActionsByStatusStage);
  const canWriteTasks = useAppSelector(selectCanWriteTasks);
  const bulkActionIcons = useContext(ConfigContext)?.bulkActionIcons || {};
  const bulkErrors = useAppSelector(selectBulkErrors);

  // KYLE: added stage here specifically as a temp fix for NBC, stage is a dupe key of step
  const isActionValid = (action: string) =>
    !task.stage
      ? actionsByStatusStage[task.status]?.actions.includes(action)
      : actionsByStatusStage[task.status]?.actions.includes(action) || actionsByStatusStage[task.status]?.stage[task.stage]?.includes(action);

  return (
    actions &&
    canWriteTasks &&
    actions && (
      <FullWidthLabelValue label="Actions">
        <ButtonList>
          {Object.keys(actions).map((action) => {
            if (!isActionValid(action)) return;
            return (
              <Button
                startIcon={
                  <PolarisIcon
                    icon={
                      actions?.[action]?.icon ||
                      bulkActionIcons[action] ||
                      (IconDictionary[action] && typeof IconDictionary[action] === 'string' ? IconDictionary[action] : 'edit')
                    }
                  />
                }
                variant="outlined"
                key={action}
                onClick={() => setDialogAction(action)}
              >
                {displayBulkActionIdent(action)}
              </Button>
            );
          })}
        </ButtonList>
        {bulkErrors.length > 0 && <BulkActionErrorModal />}
        <EntityActionDialog
          id={task.id}
          entity="task"
          action={dialogAction}
          overrideActions={actions}
          isOpen={!!dialogAction}
          setIsOpen={setDialogAction}
          afterSubmit={handleRefresh}
        />
      </FullWidthLabelValue>
    )
  );
};

export default TaskDrawerBulkActions;

const ButtonList = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing(1)};
  flex-direction: row;
  flex-wrap: wrap;
  > * {
    width: calc(50% - ${({ theme }) => theme.spacing(1)});
  }
`;

const FullWidthLabelValue = styled(LabelValue)`
  width: 100%;
`;
